import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { kindsText } from "../inputCards/typeWords";
import { Link } from "react-router";
import type { WorkflowContent, WorkflowInputDeclaration } from "../../../api/content/authoring/processContentDecoders";
import type { StepPlan } from "../../../api/content/stepPlans";
import type { ProcessCatalogue } from "../../../api/content/processDefinitions";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import ItemReferenceName from "../../items/ItemReferenceName";
import { isAbstractStep, isStepUse, referenceOf, type WorkflowDraftAction } from "../model/workflowDraft";
import { bindableSources, placementPorts, type PortCatalogue } from "../model/workflowPorts";
import OptionLabel from "../../../ui/forms/OptionLabel";
import FieldGroup from "../../../ui/forms/FieldGroup";
import Field from "../../../ui/forms/Field";
import PlannedValueField from "../../threads/stepPlans/PlannedValueField";

interface NodeInspectorProps {
  /** The project whose recorded results a default picks from, as at a Run's start. */
  projectId: string;
  draft: WorkflowContent;
  stepKey: string;
  /** Module B's plan of this placement in the draft, the last one known; `null` when none is. */
  plan: StepPlan | null;
  /** Module B's inputs for the draft, the last ones known: a first default stores them (WD-1+3 S4-G1). */
  declaredInputs: readonly WorkflowInputDeclaration[] | null;
  catalogue: ProcessCatalogue;
  ports: PortCatalogue;
  /** Problems module B's check found on this step. */
  problems: readonly string[];
  /** The bindings B's check refuses, as `<step key>.<slot>` (`editorProblems`). */
  refusedBindings: ReadonlySet<string>;
  dispatch: (action: WorkflowDraftAction) => void;
  onEditStep: (reference: string) => void;
  onAskAgent: (reference: string, name: string, kind: "step" | "workflow") => void;
}

const SOURCE_SEPARATOR = ".";
/** The select's value for "Default value": a source always holds the separator, this never does. */
const DEFAULT_CHOICE = "default";

/**
 * The selected step's settings (AU-6 node menu), and the keyboard path for everything the
 * graph does by dragging: role, start, each input's binding or Workflow default, order, edit,
 * ask, remove. A default is given with the field a Run's start gives that input's value with
 * (WD-1+3 S4-G1), where module B plans the input as taking a value.
 */
export default function NodeInspector({ projectId, draft, stepKey, plan, declaredInputs, catalogue, ports, problems, refusedBindings, dispatch, onEditStep, onAskAgent }: NodeInspectorProps) {
  const { t } = useTranslation();
  const headingId = useId();
  /** The input whose "Default value" the person chose and has given no value yet. */
  const [choosingDefault, setChoosingDefault] = useState<string | null>(null);
  const index = draft.steps.findIndex((step) => step.key === stepKey);
  const step = draft.steps[index];
  if (step === undefined) {
    return null;
  }
  const reference = referenceOf(step);
  // An abstract step pins no definition: it is named by its label (ADR-0043 D1).
  const name = (isAbstractStep(step) ? step.label : reference === null ? undefined : isStepUse(step) ? catalogue.steps.get(reference)?.name : catalogue.workflows.get(reference)?.name) ?? step.key;
  const inputs = Object.entries(placementPorts(ports, step)?.inputs ?? {});
  const sameRole = draft.steps.filter((candidate) => candidate.role === step.role);
  const position = sameRole.findIndex((candidate) => candidate.key === stepKey);
  const previous = sameRole[position - 1];
  const next = sameRole[position + 1];
  const afterNext = sameRole[position + 2];

  return (
    <section className="app-card space-y-3 p-3" aria-labelledby={headingId}>
      <header className="space-y-0.5">
        <h3 id={headingId} className="app-title text-sm font-semibold">{name}</h3>
        <p className="text-xs">
          {reference === null ? t("workflows.node.kinds.abstract_step") : <>{t(isStepUse(step) ? "workflows.edit.stepVersion" : "workflows.edit.workflowVersion")} <ItemReferenceName reference={reference} /></>}
        </p>
        <p className="app-text-muted font-mono text-xs">{t("workflows.edit.key", { key: step.key })}</p>
      </header>
      <Field layout="inline" label={t("workflows.edit.role")}>
        {(control) => (
          <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={step.role} onChange={(event) => { dispatch({ type: "setRole", key: stepKey, role: event.target.value === "input" ? "input" : "main" }); }}>
            <option value="main">{t("workflows.edit.roles.main")}</option>
            <option value="input">{t("workflows.edit.roles.input")}</option>
          </select>
        )}
      </Field>
      {isStepUse(step) ? (
        <div className="flex flex-wrap gap-3 text-sm" role="radiogroup" aria-label={t("workflows.edit.start")}>
          <span className="app-label">{t("workflows.edit.start")}</span>
          {(["automatic", "on_request"] as const).map((start) => (
            <OptionLabel key={start}>
              <input type="radio" checked={step.start === start} onChange={() => { dispatch({ type: "setStart", key: stepKey, start }); }} />
              {t(`workflows.edit.starts.${start}`)}
            </OptionLabel>
          ))}
        </div>
      ) : null}
      {isStepUse(step) && inputs.length > 0 ? (
        <FieldGroup legend={t("workflows.edit.inputs")} className="space-y-2">
          {inputs.map(([slot, input]) => {
            const bound = step.input_bindings[slot];
            const offered = bindableSources(draft, ports, stepKey, slot);
            const destination = `${stepKey}${SOURCE_SEPARATOR}${slot}`;
            const stored = draft.declared_inputs?.find((declaration) => declaration.destination === destination);
            const hasDefault = stored?.default !== undefined && stored.default !== null;
            const planned = plan?.inputs.find((candidate) => candidate.name === slot) ?? null;
            // B plans it as taking a value, and stores it or would with a first default.
            const defaultable = planned !== null && planned.takesValue && (stored !== undefined || declaredInputs?.some((declaration) => declaration.destination === destination) === true);
            const showsDefault = bound === undefined && (hasDefault || choosingDefault === destination);
            const value = bound !== undefined ? `${bound.from_step}${SOURCE_SEPARATOR}${bound.part}` : showsDefault ? DEFAULT_CHOICE : "";
            // B's check judges a binding, the fork point's too (F102 CP-04); the menu only offers what a drag would bind.
            const valid = !refusedBindings.has(destination);
            const offeredNow = bound === undefined || offered.some((source) => source.from_step === bound.from_step && source.part === bound.part);
            return (
              <div key={slot} className="space-y-1">
              <Field
                label={
                  <>
                    <span className="font-mono font-semibold">{slot}</span> · {kindsText(input.accepts, t)} · {t(input.required ? "workflows.node.port.needs.required" : "workflows.node.port.needs.optional")}
                  </>
                }
                hint={offered.length === 0 && bound === undefined && !showsDefault ? t("workflows.edit.noSources") : undefined}
                className="text-xs"
              >
                {(control) => (
                  <select
                    {...control}
                    className={FORM_SELECT_CLASS}
                    aria-invalid={!valid}
                    value={value}
                    onChange={(event) => {
                      const chosen = event.target.value;
                      setChoosingDefault(chosen === DEFAULT_CHOICE ? destination : null);
                      if (hasDefault && chosen !== DEFAULT_CHOICE) dispatch({ type: "setDefault", destination, value: null, declared: declaredInputs });
                      if (chosen === DEFAULT_CHOICE || chosen === "") {
                        if (bound !== undefined) dispatch({ type: "unbind", key: stepKey, slot });
                        return;
                      }
                      const [fromStep = "", part = ""] = chosen.split(SOURCE_SEPARATOR);
                      dispatch({ type: "bind", key: stepKey, slot, from: { from_step: fromStep, part } });
                    }}
                  >
                    <option value="">{t("workflows.edit.unbound")}</option>
                    {defaultable || hasDefault ? <option value={DEFAULT_CHOICE}>{t("workflows.edit.defaultValue")}</option> : null}
                    {offeredNow ? null : <option value={value}>{valid ? value.replace(SOURCE_SEPARATOR, " · ") : t("workflows.edit.invalidBinding", { source: value })}</option>}
                    {offered.map((source) => (
                      <option key={`${source.from_step}.${source.part}`} value={`${source.from_step}${SOURCE_SEPARATOR}${source.part}`}>
                        {source.from_step} · {source.part}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              {showsDefault && planned !== null ? (
                <PlannedValueField
                  input={planned}
                  value={stored?.default ?? null}
                  // An emptied field gives no default: the input is open again.
                  onChange={(given) => { dispatch({ type: "setDefault", destination, value: given === "" ? null : given, declared: declaredInputs }); }}
                  pickedIn={{ results: projectId }}
                  itemName={null}
                />
              ) : null}
              </div>
            );
          })}
        </FieldGroup>
      ) : null}
      {problems.length > 0 ? (
        <ul className="app-error-text space-y-0.5 text-xs">
          {problems.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      ) : null}
      <div className="flex flex-wrap gap-1">
        <AppButton variant="ghost" size="xs" disabled={previous === undefined} onClick={() => { dispatch({ type: "move", key: stepKey, before: previous?.key ?? null }); }}>
          {t("workflows.edit.moveEarlier")}
        </AppButton>
        <AppButton variant="ghost" size="xs" disabled={next === undefined} onClick={() => { dispatch({ type: "move", key: stepKey, before: afterNext?.key ?? null }); }}>
          {t("workflows.edit.moveLater")}
        </AppButton>
      </div>
      <div className="flex flex-wrap gap-2">
        {reference === null ? null : (
          <>
            {isStepUse(step) ? (
              <AppButton variant="secondary" size="xs" onClick={() => { onEditStep(reference); }}>{t("workflows.edit.editStep")}</AppButton>
            ) : (
              <Link to={appPaths.workflow(reference)} className="app-link text-xs underline">{t("workflows.edit.openSubWorkflow")}</Link>
            )}
            <AppButton variant="secondary" size="xs" onClick={() => { onAskAgent(reference, name, isStepUse(step) ? "step" : "workflow"); }}>{t("authoring.ask.button")}</AppButton>
          </>
        )}
        <AppButton variant="danger" size="xs" onClick={() => { dispatch({ type: "remove", key: stepKey }); }}>{t("workflows.edit.remove")}</AppButton>
      </div>
    </section>
  );
}
